import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import classNames from "classnames";
import { sanitize } from "dompurify";
import { EventsContext, APIContext } from "@app/pippa/provider";
import BubbleTail from "@app/pippa/components/bubbleTail";
import { Rating } from "@app/pippa/components/message/rating";
import { MessageButton } from "@app/pippa/components/message/messageButton";
import { useMarkdownToHtml } from "@app/pippa/hooks";
import { usePippaAnalytics } from "@app/pippa/hooks/usePippaAnalytics";
import {
  EventDataMessageType,
  RatingItemType,
  FeedbackDataType,
  MessageButtonType,
  ButtonTypes,
  EMPTY_RATING,
} from "@app/pippa/models";
import { getRatingScore, setRatingScore } from "@app/pippa/session";
import "./textMessage.scss";

interface Props {
  message: EventDataMessageType;
  isShowAvatar: boolean;
}

const submitButtonData: MessageButtonType = {
  id: "feedback-submit",
  type: ButtonTypes.RATING,
  title: "Submit",
  payload: "",
};

const SCHEDULING_URL =
  "https://nateraoncologyinformationsession.as.me/GISappointments";

const scheduleButtonData: MessageButtonType = {
  id: "schedule-session",
  type: ButtonTypes.WEB_URL,
  title: "Schedule Genetic Info Session",
  payload: SCHEDULING_URL,
  variant: "secondary",
};

const TextMessage: FC<Props> = ({ message, isShowAvatar }) => {
  const {
    eventsProviderState: { passedOrSkippedFlowComponentsId },
    setPassedOrSkippedFlowComponentsId,
    textMessageRef,
    userEventHandler,
  } = useContext(EventsContext);
  const { submitFeedback, apiProviderState } = useContext(APIContext);
  const { trackLinkClick } = usePippaAnalytics();
  const output = useRef<HTMLDivElement>(
    null
  ) as React.RefObject<HTMLDivElement>;
  const contentRef = useRef<HTMLDivElement>(null);
  const htmlContent = useMarkdownToHtml(message.text);
  const feedback = message.feedback as FeedbackDataType | undefined;
  const [selectedRatingItem, setSelectedRatingItem] = useState<RatingItemType>(
    () => ({
      ...EMPTY_RATING,
      scoreNumber: getRatingScore(message.id),
    })
  );

  const isDisabled = useMemo(
    () => passedOrSkippedFlowComponentsId.includes(message.id),
    [message, passedOrSkippedFlowComponentsId]
  );

  const handleContentClick = useCallback(
    (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a");
      if (!anchor) return;

      const href = anchor.getAttribute("href") ?? "";
      if (href.startsWith("tel:")) {
        trackLinkClick(undefined, href.replace("tel:", ""));
      } else if (href.startsWith("http")) {
        trackLinkClick(href);
      }
    },
    [trackLinkClick]
  );

  useEffect(() => {
    const node = contentRef.current;
    if (!node) return;

    node.addEventListener("click", handleContentClick);
    return () => node.removeEventListener("click", handleContentClick);
  }, [handleContentClick]);

  const hasSchedulingLink = useMemo(
    () => message.text.includes(SCHEDULING_URL),
    [message.text]
  );

  useImperativeHandle(textMessageRef, () => ({
    disableRating() {
      setPassedOrSkippedFlowComponentsId(message.id);
      setSelectedRatingItem((prevState) => ({
        ...prevState,
        scoreNumber: -1,
      }));
    },
  }));

  const handleSubmit = async () => {
    if (selectedRatingItem.scoreNumber === -1) return;
    const { chatId } = apiProviderState;
    setPassedOrSkippedFlowComponentsId(message.id);

    try {
      await submitFeedback(
        chatId,
        feedback?.type.toUpperCase() as string,
        selectedRatingItem.scoreNumber
      );
      setRatingScore(message.id, selectedRatingItem.scoreNumber);
      userEventHandler(`I rate it ${selectedRatingItem.scoreNumber}`, chatId);
    } catch (error) {
      console.error("Failed to submit feedback:", error);
    }
  };

  const textBubbleClassNames = classNames("text-message__bubble", {
    "text-message__with-logo": isShowAvatar,
  });

  return (
    <div
      ref={output}
      className="text-message__wrapper pippa-text-message__wrapper"
    >
      <div className={textBubbleClassNames}>
        <div
          ref={contentRef}
          className="text-message__text raw-text"
          dangerouslySetInnerHTML={{
            __html: message.isSendByUser ? sanitize(message.text) : htmlContent,
          }}
        />
        {hasSchedulingLink && (
          <MessageButton
            onClick={() => trackLinkClick(SCHEDULING_URL)}
            button={scheduleButtonData}
            isButtonDisabled={false}
          />
        )}
        {feedback && (
          <>
            <Rating
              feedback={feedback}
              rating={selectedRatingItem.scoreNumber}
              setSelectedRatingItem={setSelectedRatingItem}
              isDisabled={isDisabled}
            />
            <MessageButton
              onClick={handleSubmit}
              button={submitButtonData}
              isButtonDisabled={
                isDisabled || selectedRatingItem.scoreNumber === -1
              }
            />
          </>
        )}
      </div>
      {(isShowAvatar || !!message.isSendByUser) && (
        <BubbleTail
          isShowAvatar={isShowAvatar}
          isSendByUser={!!message.isSendByUser}
        />
      )}
    </div>
  );
};

export default TextMessage;
